import { zodResolver } from '@hookform/resolvers/zod';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import Input from 'components/forms/Input';
import RadioButton from 'components/forms/RadioButton';
import FormFieldSelect from 'components/forms/Select';
import { setHeaders } from 'features/simulations/utils';
import type { Scenario } from 'features/simulations-v2/types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

const domain = import.meta.env.VITE_SIMULATION_ROUTE;

const schema = z.object({
    vin: z.string().regex(/^[A-Z0-9]{17,}$/, 'Must be 17+ uppercase alphanumeric characters'),
    scenarioId: z.string().min(1, 'Please select a scenario'),
    driverMode: z.enum(['DRIVER_MODE_IN_SUPERVISED', 'DRIVER_MODE_OUT_AUTOMATED']),
});

type FormValues = z.infer<typeof schema>;

interface Props {
    scenarios: Scenario[];
    onCancel: () => void;
    onSuccess: () => void;
}

const CreateSimulationForm = ({ scenarios, onCancel, onSuccess }: Props) => {
    const [isError, setIsError] = useState(false);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: { driverMode: 'DRIVER_MODE_OUT_AUTOMATED' },
        mode: 'all',
    });

    const scenarioOptions = scenarios.map(s => ({ id: s.id, label: s.name }));

    const onSubmit = async (data: FormValues) => {
        setIsError(false);
        const url = `${domain}/simulator-adapter-service/api/v2/simulation/${data.vin}/initialize`;
        try {
            await fetch(url, {
                method: 'POST',
                headers: {
                    ...(await setHeaders()),
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ driverMode: data.driverMode, id: data.scenarioId }),
            });
            onSuccess();
        } catch (error) {
            console.error(error);
            setIsError(true);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className='CreateSimulationForm'>
                <Input
                    name='vin'
                    type='text'
                    label='VIN'
                    placeholder='e.g. WMA06KZZ0NM900260'
                    register={register}
                    error={errors.vin}
                />
                <FormFieldSelect
                    name='scenarioId'
                    type='text'
                    label='Scenario'
                    placeholder='Select a scenario'
                    options={scenarioOptions}
                    control={control}
                    error={errors.scenarioId}
                    onChange={() => {}}
                />
                <Controller
                    name='driverMode'
                    control={control}
                    render={({ field }) => (
                        <div className='form-group mb-0'>
                            <label>Driver Mode</label>
                            <div className='CreateSimulationForm__driverModeOptions'>
                                <RadioButton
                                    name='driverMode'
                                    checked={field.value === 'DRIVER_MODE_OUT_AUTOMATED'}
                                    onChange={() => field.onChange('DRIVER_MODE_OUT_AUTOMATED')}
                                >
                                    OUT (automated)
                                </RadioButton>
                                <RadioButton
                                    name='driverMode'
                                    checked={field.value === 'DRIVER_MODE_IN_SUPERVISED'}
                                    onChange={() => field.onChange('DRIVER_MODE_IN_SUPERVISED')}
                                >
                                    IN (supervised)
                                </RadioButton>
                            </div>
                        </div>
                    )}
                />
                {isError && (
                    <p className='CreateSimulationForm__error'>Failed to create simulation. Please try again.</p>
                )}
            </div>
            <FormDialogFooter
                onConfirmLabel='Create simulation'
                onCancel={onCancel}
                isValid={isValid}
                isLoading={isSubmitting}
            />
        </form>
    );
};

export default CreateSimulationForm;
